import { TrackmanEventFiltersKeys, TrackmanEventFiltersType, TrackmanEventModel } from 'types/trackmanEvents';
import { configure, makeAutoObservable } from 'mobx';

import { NotificationsStore } from '../NotificationsStore';
import TrackmanEventsService from '../../services/TrackmanEventsService/TrackmanEventsService';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { sortByDate } from 'utilities/dataSorters';

configure({ enforceActions: 'never' });

export const cleanEventObject: TrackmanEventModel = {
  id: '',
  title: '',
  meetingUrl: '',
  locationType: 'Online',
  location: '',
  visible: false,
  premiumEvent: false,
  startDate: '',
  imageUrl: '',
  recordingReady: false,
  videoId: '',
  videoUrl: '',
  videoLength: '',
};

const isPastDate = (isoDate: string): boolean => {
  const eventDate = new Date(isoDate);
  const currentDate = new Date();

  eventDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  return eventDate < currentDate;
};

export class TrackmanEventsStore {
  private trackmanEventsService: TrackmanEventsService;
  private ns: NotificationsStore;

  constructor(trackmanEventsService: TrackmanEventsService, notificationStore: NotificationsStore) {
    makeAutoObservable(this); //This line will automatically decorate each store property with 'observable' and each method with 'action'.
    this.trackmanEventsService = trackmanEventsService;
    this.ns = notificationStore;
  }

  eventsList: TrackmanEventModel[] = [];
  upcomingEventsList: TrackmanEventModel[] = Array(4).fill(cleanEventObject);
  onDemandEventsList: TrackmanEventModel[] = Array(4).fill(cleanEventObject);
  filteredOnDemandEventsList: TrackmanEventModel[] = Array(4).fill(cleanEventObject);
  eventFilters: TrackmanEventFiltersKeys = {};
  isLoading: boolean = true;

  getTrackmanEvents = async () => {
    try {
      this.isLoading = true;
      const response = await this.trackmanEventsService.getTrackmanEvents();
      this.eventsList = response.events;
      this.upcomingEventsList = [];
      this.onDemandEventsList = [];
      this.filteredOnDemandEventsList = [];

      this.eventsList.forEach((event: TrackmanEventModel) => {
        if (event.recordingReady) {
          this.onDemandEventsList.push(event);
        } else {
          if (isPastDate(event.startDate)) return;
          this.upcomingEventsList.push(event);
        }
      });

      this.onDemandEventsList = sortByDate(this.onDemandEventsList, (event) => event.startDate, 'DESCENDING');
      this.upcomingEventsList = sortByDate(this.upcomingEventsList, (event) => event.startDate, 'ASCENDING');
      this.filteredOnDemandEventsList = this.onDemandEventsList;
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isLoading = false;
  };

  clearFilters = () => {
    this.eventFilters = {};
    this.filteredOnDemandEventsList = this.onDemandEventsList;
  };

  removeFilterFromList = (key: string, filter: TrackmanEventFiltersType) => {
    this.eventFilters[key] = this.eventFilters[key].filter((f) => f.label !== filter.label);
    if (this.eventFilters[key].length === 0) delete this.eventFilters[key];
  };

  addFilterToList = (key: string, filter: TrackmanEventFiltersType) => {
    if (!this.eventFilters[key]) {
      this.eventFilters[key] = [];
    }
    if (!this.eventFilters[key].some((filterItem) => filterItem.label === filter.label)) {
      this.eventFilters[key].push(filter);
    }
  };

  filterEvents = (eventsList: TrackmanEventModel[]) => {
    if (Object.keys(this.eventFilters).length === 0) {
      return eventsList;
    }

    return eventsList.filter((event) => {
      return Object.entries(this.eventFilters).every(([key, filters]) => {
        return filters.some((filter) => {
          if (key in event) {
            return event[key as keyof TrackmanEventModel] === filter.value;
          }
          return false;
        });
      });
    });
  };
}

export default TrackmanEventsStore;
